<template>
  <nav v-if="crumbs.length > 1" :aria-label="$t('ui.breadcrumb.label')" class="flex items-center gap-1.5 text-xs text-text-muted">
    <template v-for="(crumb, i) in crumbs" :key="crumb.path">
      <NuxtLink
        v-if="i < crumbs.length - 1"
        :to="crumb.path"
        class="hover:text-brand-secondary transition-colors"
      >
        {{ crumb.label }}
      </NuxtLink>
      <span v-else class="text-text-primary font-medium truncate max-w-[16rem]" aria-current="page">{{ crumb.label }}</span>
      <svg v-if="i < crumbs.length - 1" aria-hidden="true" class="w-3 h-3 text-text-muted rtl:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
      </svg>
    </template>
  </nav>
</template>

<script setup lang="ts">
interface BreadcrumbItem {
  label: string;
  path: string;
}

const props = defineProps<{
  items?: BreadcrumbItem[];
}>();

const route = useRoute();
const { t } = useI18n();

const pageLabels = useBreadcrumbLabels();

// Mongo ObjectIds (24 hex chars) and UUIDs are never meaningful to a reader.
const ID_SEGMENT = /^(?:[0-9a-f]{24}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

// Built inside a function so labels follow locale switches.
function routeLabels(): Record<string, string> {
  return {
    '/': t('pages.dashboard'),
    '/warehouse': t('nav.warehouse'),
    '/warehouse/receive': t('nav.receivePackage'),
    '/warehouse/inspections': t('nav.inspections'),
    '/shipments': t('nav.shipments'),
    '/shipments/weighing-queue': t('nav.weighingQueue'),
    '/purchase-orders': t('nav.buyAndShip'),
    '/buy-and-ship': t('nav.buyAndShip'),
    '/investigations': t('nav.investigations'),
    '/investigations/service-levels': t('nav.serviceLevels'),
    '/users': t('nav.users'),
    '/resellers': t('nav.resellers'),
    '/payments': t('nav.payments'),
    '/payments/bank-transfers': t('nav.bankTransfers'),
    '/payments/cash-payments': t('nav.cashPayments'),
    '/wallet': t('nav.wallets'),
    '/chat': t('nav.chat'),
    '/memberships': t('nav.membership'),
    '/memberships/tiers': t('nav.tiers'),
    '/memberships/subscribers': t('nav.subscribers'),
    '/memberships/promotions': t('nav.promotions'),
    '/directory': t('nav.directory'),
    '/directory/applications': t('nav.applications'),
    '/directory/reviews': t('directoryManagement.reviewsTitle'),
    '/vendors': t('nav.vendors'),
    '/vendors/applications': t('nav.vendorApplications'),
    '/vendors/products': t('nav.vendorProducts'),
    '/vendors/payouts': t('nav.vendorPayouts'),
    '/returns': t('nav.returns'),
    '/orders': t('nav.orders'),
    '/products': t('nav.vendorProducts'),
    '/categories': t('nav.categories'),
    '/content': t('nav.content'),
    '/content/pages': t('nav.pages'),
    '/content/posts': t('nav.posts'),
    '/content/categories': t('nav.contentCategories'),
    '/content/media': t('nav.mediaLibrary'),
    '/settings': t('nav.settings'),
    '/settings/theme': t('settings.themeCustomization'),
    '/settings/theme/colors': t('settings.themeColors'),
    '/settings/theme/typography': t('settings.typography'),
    '/settings/theme/components': t('settings.componentStyles'),
    '/settings/theme/layout': t('settings.layout'),
    '/settings/theme/modes': t('settings.darkMode'),
    '/settings/theme/presets': t('settings.themePresets'),
    '/analytics': t('common.analytics'),
    '/reports': t('common.reports'),
    '/audit-logs': t('common.auditLogs'),
    '/support': t('common.support'),
    '/promotions': t('nav.promotions'),
  };
}

function segmentLabel(path: string, seg: string, labels: Record<string, string>): string {
  if (pageLabels.value[path]) return pageLabels.value[path];
  if (labels[path]) return labels[path];
  if (ID_SEGMENT.test(seg)) return t('ui.breadcrumb.details');
  if (seg === 'add' || seg === 'new') return t('ui.breadcrumb.add');
  return decodeURIComponent(seg).replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

const crumbs = computed<BreadcrumbItem[]>(() => {
  if (props.items?.length) return props.items;

  const labels = routeLabels();
  const segments = route.path.split('/').filter(Boolean);
  const result: BreadcrumbItem[] = [{ label: labels['/'] ?? t('pages.dashboard'), path: '/' }];

  let accumulated = '';
  for (const seg of segments) {
    accumulated += '/' + seg;
    result.push({ label: segmentLabel(accumulated, seg, labels), path: accumulated });
  }

  return result;
});
</script>
